import { motion } from 'framer-motion';
import type { OrganizationInfo, StoreTimeFormat } from '../types/api';
import { arrivalCopy } from '../config/copy';

interface ArrivalScreenProps {
  organization: OrganizationInfo;
  scheduledAt?: Date;
  onContinue: () => void;
}

/**
 * W4: three axes, and each is set on purpose.
 *
 *   zone   — the CUSTOMER's, always. `timeZone` is deliberately never passed,
 *            so Intl renders in the device's own zone. Someone in Kyiv sees
 *            Kyiv time without anything being configured for them.
 *   locale — pinned to en-US, because this app has no localisation at all
 *            (`config/copy.ts` is English, there is no i18n). Leaving it to the
 *            browser produced a half-translated page: "YOUR PRIVATE
 *            CONSULTATION" above "вторник, 3 марта 2026 г.".
 *   cycle  — the STORE's setting, the only one that comes from Portal.
 */
const DISPLAY_LOCALE = 'en-US';

function formatDate(date: Date): string {
  return new Intl.DateTimeFormat(DISPLAY_LOCALE, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

/**
 * Just the clock: `1:37 PM` or `13:37`, nothing else.
 *
 * No zone label. It used to render `1:37 PM GMT+2`, which only means anything
 * to a reader who already knows they are in GMT+2 — for everyone else it raised
 * the question it was meant to answer. The time is shown on the customer's own
 * device in the customer's own zone; that is what a clock already means.
 *
 * `hourCycle`, deliberately not `hour12`: the latter resolves to h23 or h24
 * depending on locale and has historically rendered midnight as `24:00`, and
 * when both are given `hour12` wins while `hourCycle` is silently ignored.
 *
 * A Portal that predates the field sends nothing; the locale then decides,
 * which for en-US means the 12-hour clock.
 */
function formatTime(date: Date, timeFormat?: StoreTimeFormat): string {
  return new Intl.DateTimeFormat(DISPLAY_LOCALE, {
    hour: 'numeric',
    minute: '2-digit',
    ...(timeFormat ? { hourCycle: timeFormat === '12hour' ? 'h12' : 'h23' } : {}),
  }).format(date);
}

export function ArrivalScreen({
  organization,
  scheduledAt,
  onContinue,
}: ArrivalScreenProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8, ease: 'easeInOut' }}
      className="min-h-screen w-full flex items-center justify-center bg-[#F8F6F0] px-6 py-12"
    >
      <div className="max-w-md w-full flex flex-col items-center text-center space-y-16">
        {/* Brand Logo */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="space-y-3"
        >
          <div
            className="text-5xl font-serif font-light text-[#1A1A2E] tracking-wide"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {organization.name}
          </div>
          <div className="w-16 h-px bg-[#C9A961] mx-auto" />
        </motion.div>

        {/* Appointment Details */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="space-y-6"
        >
          <p
            className="text-xs uppercase tracking-[0.2em] text-[#1A1A2E]/60 font-sans"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            {arrivalCopy.sectionLabel}
          </p>
          {scheduledAt && (
            /*
             * `lining-nums` on the whole appointment block, and it is the digits
             * that are being fixed — not the position of `PM`.
             *
             * Cormorant Garamond ships old-style figures as its default: in the
             * served Google Fonts subset `3 5 7 9` descend to -275 units and no
             * digit reaches higher than ~425, while `P` and `M` stand 628 units
             * tall. Set beside them the capitals read as a superscript, which is
             * what QA saw in `5:33 PM`. The date has the same figures — `2026`
             * — so the feature goes on the block rather than one line: two
             * stacked lines in one font at one size must not disagree about
             * which figures they use.
             *
             * The `lnum` feature is present in the subset Google actually serves
             * (verified against the woff2), and both engines apply it: on this
             * screen `5:33 PM` measures 47.95px without it and 50.0px with it, in
             * Chrome 152 and Safari 26.6 alike — the `.lf` glyphs are really
             * being substituted, so no `vertical-align` hack is needed.
             */
            <div className="space-y-3" style={{ fontVariantNumeric: 'lining-nums' }}>
              <p
                className="text-base text-[#1A1A2E]/80 font-serif"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                {formatDate(scheduledAt)}
              </p>
              <p
                className="text-base text-[#1A1A2E]/80 font-serif"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                {formatTime(scheduledAt, organization.time_format)}
              </p>
            </div>
          )}
        </motion.div>

        {/* Welcome Message */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.8 }}
          className="space-y-6 max-w-sm"
        >
          <p
            className="text-2xl text-[#1A1A2E] leading-relaxed font-serif font-light"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {arrivalCopy.welcomeHeading}
          </p>
          <p
            className="text-sm text-[#1A1A2E]/70 leading-relaxed font-sans"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            {arrivalCopy.welcomeBody}
          </p>
        </motion.div>

        {/* CTA Button */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          onClick={onContinue}
          className="group relative px-14 py-5 bg-[#1A1A2E] text-[#F8F6F0] text-xs tracking-[0.2em] uppercase overflow-hidden transition-all duration-500 hover:bg-[#C9A961] border border-[#1A1A2E] hover:border-[#C9A961]"
          style={{ fontFamily: 'Montserrat, sans-serif' }}
        >
          <span className="relative z-10">{arrivalCopy.cta}</span>
        </motion.button>
      </div>
    </motion.div>
  );
}
